import {
  DashboardState,
  EMailTemplateType,
  ENotificationTemplateType,
  ESdChangeReason,
  ILanguageModel,
  INotificationTemplateDto
} from "@kuika/kuika-cl-model";
import { INotificationLanguageTemplateDto } from "@kuika/kuika-cl-model/src/dtos/notification-template.dto";
import { Form, FormInstance } from "antd/es";
import { AxiosResponse } from "axios";
import { Guid } from "guid-typescript";
import ObjectHash from "object-hash";
import * as React from "react";
import EmailEditor from "react-email-editor";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DesignerSyncService } from "../../../services/designer-live/designer-sync-service";
import { ImageLibraryService, IUploadedResource } from "../../../services/image-library-service";
import { NotificationTemplateService } from "../../../services/notification-template-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import { DesignerTheme, getAppliedTheme, watchAppliedTheme } from "../../../shared/utility/designer-theme";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { UnsavedChangesModalModuleActionTypes } from "../../../store/unsaved-changes-modal/types";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { DeleteModal } from "../../common/delete-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { EmailBuilderLanding } from "./email-builder-landing";
import {
  applyUnlayerTheme,
  buildEmailEditorOptions,
  TEmailEditorOptions,
  toUnlayerTheme,
  TUnlayerTheme
} from "./email-editor-options";
import { buildEmailTemplateRowActions } from "./email-template-row-actions";
import {
  BLANK_EMAIL_DESIGN,
  EMAIL_LANGUAGE_OPTIONS,
  IEmailLanguageOption,
  sortEmailTemplates
} from "./email-templates";
import { NotificationTemplateEditorHeader } from "./notification-template-editor-header";
import "./notification-template.scss";
import {
  buildInlinedImageRef,
  collectResourceImageRefs,
  containsInlinedImage,
  IInlinedTemplateImage,
  inlineResourceImages,
  revertInlinedResourceImages
} from "./template-image-inlining";

/**
 * Host for the Email Builder tool: it decides between the landing list and the editor,
 * and it owns every mutation plus the Unlayer instance.
 *
 * The editor's chrome lives in child components, but `emailEditorRef`, the `EmailEditor`
 * element, the dirty hash and the save paths deliberately do NOT: moving them would risk
 * remounting the editor and dropping the design the user is working on.
 */

interface IUnlayerImageUploadFile {
  attachments: File[];
}

interface IUnlayerImageUploadResult {
  progress: number;
  url?: string;
}

/** What `exportHtml` hands back: the design object and the rendered HTML. */
interface IUnlayerExport {
  design: object;
  html: string;
}

/** `design:updated` payload. `design:migrated` is Unlayer's own schema upgrade. */
interface IUnlayerDesignUpdatedEvent {
  type?: string;
}

// The ref stays untyped: `react-email-editor`'s shipped types describe the editor's
// methods on the component itself, while the running build exposes them under `.editor`,
// so the declared type cannot be used for the calls this file actually makes. The payload
// types above cover everything that crosses back out of the editor.

export interface INotificationTemplateCanvasState {
  isLoading: boolean;
  /** Where the header's UPDATE stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where the name sheet's create stands. Owned by `SaveStatus`, never set directly. */
  createStatus: KuikaButtonStatus;
  /** The template an UPDATE saved, revealed in the list the editor closes onto. */
  revealTemplateId: string | null;
  /** Id of the template a duplicate is in flight for, so only THAT row spins. */
  duplicatingId?: string;
  templateToDelete?: INotificationTemplateDto;
  submitButtonDisabledStatus: boolean;
  templateName: string;
  isNameOnEdit: boolean;
  languages: ILanguageModel[];
  currentLanguage: ILanguageModel | undefined;
  isMlEnabled: boolean;
}

export interface INotificationTemplateCanvasProps {
  showModal: boolean;
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  setShowModal: (showModal: boolean) => void;
}

class NotificationTemplateCanvas extends PureSdComponent<
  INotificationTemplateCanvasProps & IWithSDProps & IWithContextProp,
  INotificationTemplateCanvasState
> {
  emailEditorRef: React.RefObject<any>;

  objectHash: string = "";
  // Resource images that were swapped for data URIs when the current design was loaded
  // (KOA-18358). exportForPersist reverses exactly these substitutions, so what gets saved still
  // holds resource URLs. Re-assigned on every load — including to an empty list — so substitutions
  // from a previously opened template can never leak into another template's save.
  inlinedImages: IInlinedTemplateImage[] = [];

  // False until loadDesignJson has handed the design to Unlayer AND recorded its objectHash
  // baseline. Loading is asynchronous (image bytes are fetched first), so without this the
  // dirty-checks below would compare the editor against a baseline taken from a different
  // representation and report phantom unsaved changes.
  isDesignLoaded: boolean = false;

  formRef = React.createRef<FormInstance>();

  /**
   * UPDATE's state machine. An update closes the editor onto the list, so the ✓ is held on the
   * button first and the editor leaves after it — the order `SaveStatus` owns. Only the
   * user's own UPDATE runs through it; the silent saves (switching or adding a language) do not.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /**
   * The create's state machine, shown on the name sheet's confirm. A create opens the new
   * template's editor, which replaces the list and the sheet with it — so the ✓ is held first and
   * the editor opens after it, and `canStart` keeps an Enter pressed during the ✓ from creating a
   * second template.
   */
  private readonly createStatus: SaveStatus = new SaveStatus((createStatus: KuikaButtonStatus): void => {
    this.setState({ createStatus });
  });

  /**
   * True from a successful UPDATE until its ✓ has been held and the editor closed. The machine
   * goes back to idle a moment before it hands over, and that re-render must not run the
   * navigation check in `componentDidUpdate` — the design is not re-baselined after a save, so the
   * check would read the saved template as dirty and raise "Unsaved Changes" over it.
   */
  private isClosingAfterSave: boolean = false;

  /**
   * The options the open editor was created with. Rebuilt only while no editor exists and then
   * frozen for its life: `react-email-editor` re-creates the editor — dropping the unsaved design —
   * whenever they change, so a theme flip must not reach them (see `email-editor-options.ts`).
   */
  private editorOptions: TEmailEditorOptions | null = null;

  /** The Unlayer theme the editor currently shows, so a flip to that same theme is not re-sent. */
  private editorTheme: TUnlayerTheme | null = null;

  private stopWatchingTheme: (() => void) | null = null;

  constructor(props: INotificationTemplateCanvasProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.emailEditorRef = React.createRef<any>();
    this.state = {
      isLoading: false,
      saveStatus: "idle",
      createStatus: "idle",
      revealTemplateId: null,
      duplicatingId: undefined,
      templateToDelete: undefined,
      submitButtonDisabledStatus: true,
      templateName: "",
      isNameOnEdit: false,
      languages: [],
      currentLanguage: {
        id: Guid.create().toString(),
        name: "Default",
        shortName: "Default",
        isDefault: true
      },
      isMlEnabled: false
    };
  }

  componentDidMount = () => {
    const selectedTemplate = this.props.sd.editingNotificationTemplate;
    if (selectedTemplate) {
      this.setFieldsAndUpdate(
        selectedTemplate.name,
        selectedTemplate.multiLanguageEnabled,
        selectedTemplate.languageTemplates
      );
      this.objectHash = this.getObjectHash(JSON.parse(selectedTemplate.design));
    }
    this.stopWatchingTheme = watchAppliedTheme(this.syncEditorTheme);
  };

  componentDidUpdate(prevProps: INotificationTemplateCanvasProps & IWithSDProps & IWithContextProp) {
    const selectedTemplate = this.props.sd.editingNotificationTemplate;
    if (selectedTemplate && selectedTemplate.id !== prevProps.sd.editingNotificationTemplate?.id) {
      this.setFieldsAndUpdate(
        selectedTemplate.name,
        selectedTemplate.multiLanguageEnabled,
        selectedTemplate.languageTemplates
      );
    }

    // A pending navigation asks the editor to close — but not while UPDATE is running or holding
    // its ✓: that save closes the editor itself once the ✓ has been seen.
    if (this.props.nextCallback && this.saveStatus.canStart && !this.isClosingAfterSave) {
      this.handleCloseNotification();
      return;
    }
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.createStatus.dispose();
    this.stopWatchingTheme?.();
    if (this.emailEditorRef.current)
      this.emailEditorRef.current?.editor?.removeEventListener("design:updated", this.editorDesignUpdatedHandler);
  }

  setFieldsAndUpdate = (
    name?: string,
    mlEnability?: boolean,
    languageTemplates?: INotificationLanguageTemplateDto[]
  ) => {
    this.formRef.current?.resetFields();
    const formValues = {
      name: name || ""
    };
    this.formRef.current?.setFieldsValue(formValues);
    this.loadDesign();
    // "Default" is a synthetic pseudo-language: it is not a row in `languageTemplates`,
    // it IS the template's top-level design/body.
    let languages = [
      {
        id: Guid.create().toString(),
        name: "Default",
        shortName: "Default",
        isDefault: true
      }
    ];
    if (languageTemplates) {
      languageTemplates.forEach((languageTemplate: INotificationLanguageTemplateDto) => {
        if (languages.find((x) => x.id === languageTemplate.templateLanguageId)) return;
        const language = {
          id: languageTemplate.templateLanguageId,
          name: languageTemplate.templateLanguageName,
          shortName: languageTemplate.templateLanguageShortName,
          isDefault: false
        };
        languages.push(language);
      });
    }
    this.setState({
      isNameOnEdit: false,
      templateName: name || "",
      isMlEnabled: mlEnability || false,
      languages: languages || [],
      currentLanguage: languages.find((x) => x.isDefault) || languages[0]
    });
  };

  onReady = () => {
    this.emailEditorRef.current?.editor.addEventListener("design:updated", this.editorDesignUpdatedHandler);
    this.emailEditorRef.current?.editor.registerCallback("image", this.handleUnlayerImageUpload);
    // The theme may have flipped between building the options and the editor coming up.
    this.syncEditorTheme(getAppliedTheme());
  };

  getEditorOptions = (): TEmailEditorOptions => {
    const hasEditor: boolean = !!this.emailEditorRef.current?.editor;
    if (this.editorOptions === null || !hasEditor) {
      const theme: DesignerTheme = getAppliedTheme();
      this.editorOptions = buildEmailEditorOptions(theme);
      this.editorTheme = toUnlayerTheme(theme);
    }
    return this.editorOptions;
  };

  /**
   * Follows a designer theme flip in the open editor through Unlayer's live `setTheme`, never by
   * re-creating it. With no editor open there is nothing to do: the next one is created in the
   * theme painted at that moment.
   */
  syncEditorTheme = (theme: DesignerTheme): void => {
    const next: TUnlayerTheme = toUnlayerTheme(theme);
    if (next === this.editorTheme) return;

    const isApplied: boolean = applyUnlayerTheme(this.emailEditorRef.current?.editor, next);
    if (isApplied) this.editorTheme = next;
  };

  handleUnlayerImageUpload = async (
    file: IUnlayerImageUploadFile,
    done: (result: IUnlayerImageUploadResult) => void
  ) => {
    const attachment = file.attachments?.[0];
    if (!attachment) {
      done({ progress: 100 });
      return;
    }
    try {
      const uploaded: IUploadedResource = await ImageLibraryService.UploadFile(attachment);

      // Hand Unlayer the bytes, never the URL. Given a URL on a private/loopback API host the
      // editor cannot render it at all: its iframe is blocked from that address space, and its
      // server-side fallback proxy (api.unlayer.com/builder/v3/tools/image) answers 422 because it
      // cannot reach the host either. Registering the pair here lets exportForPersist swap the data
      // URI back for the stored URL, so the image is still saved as a plain resource reference.
      const ref: IInlinedTemplateImage | null = buildInlinedImageRef(uploaded.resourceUrl, uploaded.dataUrl);
      if (!ref) {
        done({ progress: 100, url: uploaded.resourceUrl });
        return;
      }

      this.inlinedImages = [
        ...this.inlinedImages.filter((x: IInlinedTemplateImage): boolean => x.resourceUrl !== ref.resourceUrl),
        ref
      ];
      done({ progress: 100, url: ref.dataUrl });
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      done({ progress: 100 });
    }
  };

  editorDesignUpdatedHandler = (data: IUnlayerDesignUpdatedEvent): void => {
    // Unlayer migrating an old schema on load is not user intent, so it must not mark the
    // template dirty.
    if (data.type === "design:migrated") return;

    this.setSubmitButtonDisabledStatus();
  };

  // Hashes the design in its PERSISTED shape, with any inlined image swapped back for its resource
  // URL. Two reasons, both measured (KOA-18358): it keeps the hash comparable regardless of whether
  // the design currently carries inlined images — the componentDidMount baseline is read straight
  // from storage, which never does — and it keeps this hot path cheap. Unlayer fires design:updated
  // on every edit, and hashing a design with one embedded 183 KB image cost 17ms per event against
  // 1ms for the reverted form.
  getObjectHash(templateJson: object) {
    const designJson: string = revertInlinedResourceImages(JSON.stringify(templateJson), this.inlinedImages);
    return ObjectHash({
      name: this.state.templateName,
      design: designJson,
      multiLanguageEnabled: this.state.isMlEnabled
    });
  }

  handleCloseNotification = () => {
    // UPDATE is running or holding its ✓, and it closes the editor itself afterwards. Closing now
    // would unmount Unlayer mid-export and drop the save.
    if (!this.saveStatus.canStart) return;
    if (!this.emailEditorRef?.current) {
      this.closeNotification();
      return;
    }

    // Nothing the user did can be pending while the design is still loading, so close rather than
    // compare against a baseline that is not established yet.
    if (!this.isDesignLoaded) {
      this.closeNotification();
      return;
    }

    if (this.emailEditorRef?.current?.editor?.exportHtml) {
      this.emailEditorRef?.current?.editor?.exportHtml((data) => {
        if (this.objectHash === this.getObjectHash(data?.design)) {
          this.closeNotification();
          return;
        }
        this.props.setShowModal(true);
      });
    }
  };

  closeNotification = () => {
    if (runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      return;
    }
    this.props?.sd?.clearNotificationTemplatePanel();
    this.props?.sd?.setCreateNotificationTemplateActive();
  };

  onLoad = () => {
    this.loadDesign();
  };

  loadDesign = (languageTemplate?: INotificationLanguageTemplateDto) => {
    this.setState({ submitButtonDisabledStatus: true }, () => {
      if (languageTemplate) {
        void this.loadDesignJson(languageTemplate.design);
        return;
      }
      if (
        this.props.sd.editingNotificationTemplate &&
        this.props.sd.editingNotificationTemplate.design &&
        this.props.sd.editingNotificationTemplate.templateType === ENotificationTemplateType.Email
      ) {
        void this.loadDesignJson(this.props.sd.editingNotificationTemplate.design);
      }
    });
    this.setLoading(false);
  };

  // Embeds every re-homed resource image in the design as a "data:" URI before Unlayer sees it, so
  // the editor iframe never has to fetch our API itself — a request Chrome's Local Network Access
  // policy blocks outright whenever the API host sits on a private network (KOA-18358). The
  // substitutions are remembered on this.inlinedImages and undone by exportForPersist, so the
  // stored template keeps holding plain resource URLs exactly as before.
  loadDesignJson = async (designJson: string): Promise<void> => {
    // Switching language reloads the design, so stand the dirty-checks down until the new baseline
    // is recorded rather than letting them compare against the outgoing one.
    this.isDesignLoaded = false;
    try {
      const inlinedDesignJson: string = await this.inlineDesignImages(designJson);

      if (!this.emailEditorRef?.current?.editor) return;
      this.emailEditorRef.current.editor.loadDesign(JSON.parse(inlinedDesignJson));
      this.emailEditorRef.current.editor.exportHtml((data) => {
        this.objectHash = this.getObjectHash(data.design);
        this.isDesignLoaded = true;
      });
    } catch (error: unknown) {
      // Loading is now async, so a malformed stored design would otherwise surface as an unhandled
      // rejection and leave the builder silently blank instead of reporting anything.
      KMainFunctions.exceptionHandler(error);
    }
  };

  inlineDesignImages = async (designJson: string): Promise<string> => {
    const refs: IInlinedTemplateImage[] = collectResourceImageRefs(designJson);
    this.inlinedImages = refs;
    if (refs.length === 0) return designJson;

    await Promise.all(
      refs.map(async (ref: IInlinedTemplateImage): Promise<void> => {
        try {
          ref.dataUrl = await ImageLibraryService.GetResourceAsDataUrl(ref.resourceId);
        } catch {
          // An unreadable image keeps its original URL: it renders exactly as badly as it does
          // today rather than taking the whole design down with it.
          ref.dataUrl = "";
        }
      })
    );

    return inlineResourceImages(designJson, refs);
  };

  /**
   * Runs one create from the name sheet. The ✓ is held on the sheet's confirm, then the new
   * template's editor opens and replaces the list — and the sheet with it. A failure leaves the
   * sheet up on "Try again".
   */
  private runCreate = async (request: () => Promise<AxiosResponse>): Promise<void> => {
    if (!this.createStatus.canStart) return;
    this.createStatus.start();
    this.setLoading(true);
    try {
      const response: AxiosResponse = await request();
      if (response.status !== 200) {
        this.createStatus.fail();
        return;
      }
      this.createStatus.succeed((): void => {
        this.props.sd.notificationTemplateType = ENotificationTemplateType.Email;
        this.props.sd.setEditingNotificationTemplate(response.data, false, this.props.sd.notificationTemplateType);
        this.props.sd.getNotificationTemplates();
      });
    } catch (error) {
      this.createStatus.fail();
      KMainFunctions.displayErrorNotification(error);
    } finally {
      // The spinner also hides the Unlayer branding cover, so it must never be left on.
      this.setLoading(false);
    }
  };

  /** Creates a blank template under the name the create sheet asked for, and opens it. */
  create = (name: string): Promise<void> => {
    const templateObj = {} as INotificationTemplateDto;
    templateObj.kProject_Id = this.props.sd.project.id;
    templateObj.name = name;
    templateObj.templateType = ENotificationTemplateType.Email;
    templateObj.multiLanguageEnabled = false;
    templateObj.design = BLANK_EMAIL_DESIGN;
    return this.runCreate((): Promise<AxiosResponse> => NotificationTemplateService.Create(templateObj));
  };

  /** Materialises one of the free catalog templates under the given name and opens it. */
  onTemplateSelect = (id: string, name: string): Promise<void> =>
    this.runCreate((): Promise<AxiosResponse> =>
      NotificationTemplateService.CreateByTemplateId(id, name, this.props.sd.project.id)
    );

  createTemplateByLanguage = (language: ILanguageModel) => {
    this.setLoading(true);
    if (!this.props.sd.editingNotificationTemplate) {
      this.setLoading(false);
      return;
    }

    let templateObj = {} as INotificationTemplateDto;
    templateObj = this.props.sd.editingNotificationTemplate;

    if (!this.emailEditorRef || !this.emailEditorRef.current) {
      this.setLoading(false);
      return;
    }
    this.exportForPersist((designJson: string, bodyJson: string) => {
      templateObj.body = bodyJson;
      templateObj.design = designJson;
      // A new locale starts as a copy of what is currently on screen, not as a blank.
      const languageTemplate: INotificationLanguageTemplateDto = {
        design: designJson,
        body: bodyJson,
        templateLanguageId: language.id,
        templateLanguageName: language.name,
        templateLanguageShortName: language.shortName,
        id: Guid.create().toString()
      };
      templateObj.languageTemplates = templateObj.languageTemplates
        ? [...templateObj.languageTemplates, languageTemplate]
        : [languageTemplate];
      // The silent save has to carry the flag too, or the locale would persist against a
      // template still marked single-language.
      templateObj.multiLanguageEnabled = true;
      this.upddate(templateObj, true, true);
    });
    this.setLoading(false);
  };

  update = (updateWithoutValidation: boolean, callback?: () => void): void => {
    // The user's own UPDATE (the header's, or the close dialog's "Update & close") is the one the
    // button reports on; the silent saves behind a language switch are not. The commit starts at
    // the click, not once Unlayer's async export answers: until then a pending navigation would
    // read a free machine, close the editor, and the export and its PUT would never run.
    const isCommit: boolean = !callback;
    if (isCommit) {
      if (!this.saveStatus.canStart) return;
      this.saveStatus.start();
    }
    const abandonCommit = (): void => {
      if (isCommit) this.saveStatus.reset();
    };
    this.setState({ isLoading: true });
    if (!this.props.sd.editingNotificationTemplate) {
      this.setLoading(false);
      abandonCommit();
      return;
    }

    let templateObj = {} as INotificationTemplateDto;
    templateObj = this.props.sd.editingNotificationTemplate;

    const languageTemplate = templateObj.languageTemplates?.find(
      (x) => x.templateLanguageId === this.state.currentLanguage?.id
    );

    if (this.props.sd.notificationTemplateType === ENotificationTemplateType.Email) {
      if (!this.emailEditorRef || !this.emailEditorRef.current) {
        this.setLoading(false);
        abandonCommit();
        return;
      }
      this.exportForPersist((designJson: string, bodyJson: string) => {
        // While a real locale is selected the top-level (Default) slot is preserved and the
        // export lands in that locale's row instead.
        const _design: string | undefined =
          languageTemplate && this.state.currentLanguage?.shortName !== "Default"
            ? this.props.sd.editingNotificationTemplate?.design
            : designJson;
        const _html: string | undefined =
          languageTemplate && this.state.currentLanguage?.shortName !== "Default"
            ? this.props.sd.editingNotificationTemplate?.body
            : bodyJson;
        templateObj.name = this.state.templateName!;
        templateObj.design = _design as string;
        templateObj.body = _html as string;
        templateObj.multiLanguageEnabled = this.state.isMlEnabled;
        templateObj.languageTemplates = templateObj.languageTemplates?.map((x) => {
          if (x.templateLanguageId === this.state.currentLanguage?.id) {
            x.design = designJson;
            x.body = bodyJson;
          }
          return x;
        });
        this.upddate(templateObj, !!callback, updateWithoutValidation);
        if (callback) callback();
      }, abandonCommit);
    } else {
      this.setLoading(false);
      abandonCommit();
    }
  };

  upddate = async (
    templateObj: INotificationTemplateDto,
    doNotTriggerNotification?: boolean,
    updateWithoutValidation?: boolean
  ) => {
    // The only save path, and therefore the only place the lost-update guard can sit. It is
    // armed by `setEditingNotificationTemplate`, which reports the entity to presence with
    // the matching stale kind.
    const proceed: boolean = await DesignerSyncService.checkStaleWrite(
      "notificationTemplate.changed",
      templateObj.id,
      templateObj.name ?? "Notification template"
    );
    // A commit's machine was started by `update` at the click.
    const isCommit: boolean = !doNotTriggerNotification;
    if (!proceed) {
      this.setLoading(false);
      if (isCommit) this.saveStatus.reset();
      return;
    }
    await NotificationTemplateService.Update(templateObj.id, templateObj, updateWithoutValidation)
      .then((response) => {
        if (!response) {
          if (isCommit) this.saveStatus.fail();
          return;
        }
        this.props.sd.getNotificationTemplates();
        if (isCommit) {
          this.props.setShowModal(false);
          // The ✓ first, then the editor closes onto the list, where the saved row is revealed.
          this.isClosingAfterSave = true;
          this.saveStatus.succeed((): void => {
            this.isClosingAfterSave = false;
            // A save that is part of leaving the tool has no list to land on.
            if (!this.props.nextCallback) {
              this.setState({ revealTemplateId: templateObj.id });
            }
            this.closeNotification();
          });
        }
      })
      .catch((error) => {
        if (isCommit) this.saveStatus.fail();
        KMainFunctions.displayErrorNotification(error);
      })
      .finally(() => {
        this.setLoading(false);
      });
  };

  setLoading = (isLoading: boolean) => {
    this.setState({ isLoading });
  };

  // Single seam for every path that persists a template. Exports from Unlayer, undoes the data-URI
  // substitutions made at load time, and refuses to continue if any base64 image survived — writing
  // one would bloat the row and break mail rendering, since mail clients block data: images and the
  // server's CID inliner only recognises resource URLs.
  exportForPersist = (callback: (designJson: string, bodyJson: string) => void, onAbort?: () => void): void => {
    if (!this.emailEditorRef?.current?.editor) {
      // The spinner also hides the Unlayer branding cover, so it must never be left on.
      this.setLoading(false);
      onAbort?.();
      return;
    }

    this.emailEditorRef.current.editor.exportHtml((data: IUnlayerExport) => {
      const { design, html } = data;
      const designJson: string = revertInlinedResourceImages(JSON.stringify(design), this.inlinedImages);
      const bodyJson: string = revertInlinedResourceImages(JSON.stringify(html), this.inlinedImages);

      if (containsInlinedImage(designJson) || containsInlinedImage(bodyJson)) {
        this.setLoading(false);
        KMainFunctions.displayErrorNotification(
          "Template images could not be restored to their stored locations, so the template was not saved. Please reload the email builder and try again."
        );
        onAbort?.();
        return;
      }

      callback(designJson, bodyJson);
    });
  };

  setSubmitButtonDisabledStatus() {
    // Unlayer emits design:updated while loadDesign is still settling; comparing then would enable
    // Save on a template the user has not touched.
    if (!this.isDesignLoaded) return;
    if (!this.emailEditorRef?.current?.editor?.exportHtml) return;
    this.emailEditorRef.current.editor.exportHtml((data) => {
      if (this.objectHash === this.getObjectHash(data.design)) {
        this.setState({ submitButtonDisabledStatus: true });
        return;
      }

      this.setState({ submitButtonDisabledStatus: false });
    });
  }

  isKuikaDeveloper = (): boolean => {
    return KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username);
  };

  handleSubmitNameChange = (name: string) => {
    this.setState({
      isNameOnEdit: false,
      templateName: name
    });
  };

  handleAbortNameChange = () => {
    if (!this.state.templateName) {
      KMainFunctions.displayWarningNotification("Please enter a valid name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.templateName
    });
    this.setState({ isNameOnEdit: false });
  };

  /**
   * Runs the name field's own rules and resolves the accepted value. Awaiting matters: the
   * uniqueness rule is async and the field is mounted fresh each time the pencil is
   * clicked, so reading the last validation result would let a duplicate through.
   */
  validateName = async (): Promise<string | undefined> => {
    try {
      const values = await this.formRef.current?.validateFields(["name"]);
      return values?.name;
    } catch {
      return undefined;
    }
  };

  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;

    const templates = this.props.sd.notificationTemplates;

    if (templates.length === 0) return;

    if (
      templates.some(
        (tmp) =>
          tmp.name.toLowerCase() === value.toLowerCase() && tmp.id !== this.props.sd.editingNotificationTemplate?.id
      )
    )
      throw new Error("This Template Name is used before.");
  };

  handleAddLanguage = (language: IEmailLanguageOption) => {
    const lang: ILanguageModel = {
      id: Guid.create().toString(),
      name: language.label,
      shortName: language.value,
      isDefault: false
    };
    const languages = this.state.languages.concat(lang);
    // Adding a locale is what turns multi-language on: the flag on its own produced a
    // template that claimed to be multi-language with nothing to switch between.
    this.setState({
      languages,
      currentLanguage: lang,
      isMlEnabled: true
    });
    this.createTemplateByLanguage(lang);
  };

  onChangeLanguage = (shortName: string) => {
    this.setLoading(true);
    const lang = this.state.languages.find((x) => x.shortName === shortName);
    if (!lang) {
      this.setLoading(false);
      return;
    }

    // Save the outgoing locale silently, then swap what the editor is showing.
    this.update(true, () => {
      this.setState({ currentLanguage: lang });
      this.loadDesign(
        lang.isDefault
          ? undefined
          : this.props.sd.editingNotificationTemplate?.languageTemplates?.find((x) => x.templateLanguageId === lang.id)
      );
    });
  };

  getAddableLanguages = (): IEmailLanguageOption[] =>
    EMAIL_LANGUAGE_OPTIONS.filter(
      (language: IEmailLanguageOption): boolean =>
        !this.state.languages.find((lang: ILanguageModel): boolean => lang.shortName === language.value)
    );

  /**
   * Opening a template is always queued through the unsaved-changes callback, never done
   * directly: that is what runs the editor's dirty check first. Clearing before setting
   * is what re-arms presence and the stale-write stamp for the new entity.
   */
  openTemplate = (template?: INotificationTemplateDto): void => {
    this.props.setNextCallbackFunction(() => {
      this.props.sd.clearNotificationTemplatePanel();
      if (template) this.props.sd.setEditingNotificationTemplate(template, false, template.templateType);
      else this.props.sd.setCreateNotificationTemplateActive();
    });
  };

  /**
   * Duplicate and delete belong to the host rather than the list page, because the editor
   * header offers them too — the Emails panel used to, and it stayed on screen while a
   * template was open.
   */
  duplicateTemplate = (templateObj: INotificationTemplateDto): void => {
    this.setState({ duplicatingId: templateObj.id });
    let name = `${templateObj.name} Copy`;
    let i = 1;
    while (this.props.sd.notificationTemplates.some((template) => template.name === name)) {
      name = `${templateObj.name} Copy${i}`;
      i++;
    }
    const obj = {
      NotificationIdToDuplicate: templateObj.id,
      NewNotificationName: name
    };
    NotificationTemplateService.Duplicate(obj)
      .then((response) => {
        if (response.status === 200) {
          this.props.sd.getNotificationTemplates();
          this.openTemplate(response.data);
        }
      })
      .catch((error) => {
        KMainFunctions.displayErrorNotification("An error occurred!", error);
      })
      .finally(() => {
        this.setState({ duplicatingId: undefined });
      });
  };

  requestDeleteTemplate = (template: INotificationTemplateDto): void => {
    this.setState({ templateToDelete: template });
  };

  deleteTemplate = (template: INotificationTemplateDto): void => {
    NotificationTemplateService.Delete(template.id, this.props.sd.project.id)
      .then((response) => {
        if (response) {
          this.props.sd.getNotificationTemplates();
          const editing: INotificationTemplateDto | undefined = this.props.sd.editingNotificationTemplate;
          if (editing && editing.id === template.id) {
            // The open template is the one that just went away. Queueing a switch here
            // would raise "Unsaved Changes" for a row that no longer exists — and its
            // SAVE & CLOSE would PUT to a deleted id. Close it outright instead.
            this.props.sd.clearNotificationTemplatePanel();
            this.props.sd.setCreateNotificationTemplateActive();
          }
        }
      })
      .catch((error) => {
        KMainFunctions.displayErrorNotification("An error occurred!", error);
      });
  };

  handleSwitchTemplate = (template: INotificationTemplateDto): void => {
    this.openTemplate(template);
  };

  handleCreateNewFromSwitcher = (): void => {
    this.openTemplate(undefined);
  };

  getEmailTemplates = (): INotificationTemplateDto[] =>
    sortEmailTemplates(
      (this.props.sd?.notificationTemplates ?? []).filter(
        (item: INotificationTemplateDto): boolean => item.templateType === ENotificationTemplateType.Email
      )
    );

  renderEditor(): React.ReactNode {
    return (
      <Form name="ntCreateModal" ref={this.formRef} layout="vertical" className="kuika-email-builder__editor-form">
        {/* The bar is the column's first row, OUTSIDE the editor's gutter — it is the
            editor's chrome and reaches the panel's own edges. */}
        <NotificationTemplateEditorHeader
          templateName={this.state.templateName}
          isNameOnEdit={this.state.isNameOnEdit}
          canRename={this.props.sd.editingNotificationTemplate?.templateStatus === EMailTemplateType.Default}
          onStartRename={() => this.setState({ isNameOnEdit: true })}
          onSubmitName={this.handleSubmitNameChange}
          onAbortRename={this.handleAbortNameChange}
          nameValidator={this.existingNameValidator}
          validateName={this.validateName}
          templates={this.getEmailTemplates()}
          currentTemplate={this.props.sd.editingNotificationTemplate}
          onSwitchTemplate={this.handleSwitchTemplate}
          onCreateNew={this.handleCreateNewFromSwitcher}
          isMlEnabled={this.state.isMlEnabled}
          onMlEnabledChange={(checked: boolean) => this.setState({ isMlEnabled: checked })}
          languages={this.state.languages}
          currentLanguage={this.state.currentLanguage}
          onChangeLanguage={this.onChangeLanguage}
          addableLanguages={this.getAddableLanguages()}
          onAddLanguage={this.handleAddLanguage}
          onBack={this.handleCloseNotification}
          onUpdate={() => this.update(false)}
          updateStatus={this.state.saveStatus}
          rowActions={buildEmailTemplateRowActions({
            onDuplicate: this.duplicateTemplate,
            onRequestDelete: this.requestDeleteTemplate
          })}
        />
        <div className="kuika-email-builder__editor">
          {/* The sheet the editor sits on. The branding cover is placed against its corner. */}
          <div className="kuika-email-builder__frame">
            {/* Covers Unlayer's own footer branding for customers. Kuika developers see it,
                and it lifts while the spinner is up. */}
            <div
              className="kuika-email-builder__branding-cover"
              style={{
                display: this.state.isLoading || this.isKuikaDeveloper() ? "none" : "block"
              }}
            ></div>
            {/* Marks the top of Unlayer's footer bar in dark, where its own paint leaves no edge. */}
            <div className="kuika-email-builder__branding-rule"></div>
            <EmailEditor
              style={{ height: "100%" }}
              // The wrapper's own floor is 500px by default; this hands it the frame's instead,
              // so on a short window the two cannot disagree and clip the editor's bottom edge.
              minHeight="var(--kuika-email-editor-min-height)"
              ref={this.emailEditorRef}
              onLoad={this.onLoad}
              onReady={this.onReady}
              projectId={1071}
              options={this.getEditorOptions()}
            />
          </div>
        </div>
      </Form>
    );
  }

  render() {
    return (
      <>
        {this.props.sd && this.props.sd.dashboardState === DashboardState.notificationTemplate && (
          <div className="kuika-email-builder__root">
            <CloseConfirmationModal
              itemName={this.state.templateName}
              closeType={"Email Builder"}
              triggerModal={this.props.showModal}
              isLoading={this.state.isLoading}
              updateAndCloseHandler={() => this.update(false)}
              closeWithoutSaveHandler={() => {
                this.props.setShowModal(false);
                this.closeNotification();
              }}
              cancel={() => {
                this.props.setNextCallbackFunction();
                this.props.setShowModal(false);
              }}
            />
            {/* The spinner is the editor's save lock. On the list it only doubled the create's own
                indicators — the gallery's card skeleton and the name sheet's busy Create — so it
                is off there. */}
            <KuikaSpinnerSmall spinning={this.state.isLoading && !!this.props.sd.editingNotificationTemplate}>
              {this.props.sd.editingNotificationTemplate ? (
                this.renderEditor()
              ) : (
                <EmailBuilderLanding
                  isCreating={this.state.isLoading}
                  createStatus={this.state.createStatus}
                  duplicatingId={this.state.duplicatingId}
                  revealTemplateId={this.state.revealTemplateId}
                  onTemplateRevealed={() => this.setState({ revealTemplateId: null })}
                  onCreateBlank={this.create}
                  onCreateFromTemplate={this.onTemplateSelect}
                  onOpenTemplate={this.openTemplate}
                  onDuplicate={this.duplicateTemplate}
                  onRequestDelete={this.requestDeleteTemplate}
                />
              )}
            </KuikaSpinnerSmall>
            {/* Owned by the host so the confirmation survives the surface it was opened
                from — the editor closes itself when the open template is the one deleted. */}
            <DeleteModal
              itemName={this.state.templateToDelete?.name ?? ""}
              triggerModal={!!this.state.templateToDelete}
              deleteItem={() => {
                const target: INotificationTemplateDto | undefined = this.state.templateToDelete;
                if (target) this.deleteTemplate(target);
              }}
              deleteType={"Email Template"}
              closeModal={() => this.setState({ templateToDelete: undefined })}
            />
          </div>
        )}
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch: Dispatch<UnsavedChangesModalModuleActionTypes>) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const notificationTemplateCanvas = connect(
  mapStateToProps,
  mapDispatchToProps
)(withContext(withSD<INotificationTemplateCanvasProps>(NotificationTemplateCanvas)));
export { notificationTemplateCanvas as NotificationTemplateCanvas };
